import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Rocket, ArrowRight, CheckCircle2, Sparkles, X } from 'lucide-react';
import {
  Card, CardBody, Button, Loading, ErrorNotice,
} from '../../components/ui/index.js';
import { PageHero } from '../../components/PageHero.jsx';
import { StepDot } from '../../components/OnboardingChecklist.jsx';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';

const EASE = [0.2, 0.7, 0.2, 1];

/**
 * The guided setup.
 *
 * Two ideas carry this screen. The first is that every step is *derived* from
 * the data, so it cannot tell you something is missing when it is not — the
 * previous checklist kept five booleans and got this wrong for anyone who took
 * bank transfers instead of connecting a gateway.
 *
 * The second is that the steps are not all the same kind of thing. Some block
 * you from being paid at all; the rest are worth doing and can be ignored
 * indefinitely. A shop using payment links and a till never needs an API key,
 * and being chased for one forever is how a setup screen teaches people to
 * ignore it.
 */
function StepCard({ step, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05, ease: EASE }}
    >
      <Card className={step.done ? 'opacity-70' : ''}>
        <CardBody className="flex items-start gap-4">
          <div className="mt-0.5"><StepDot done={step.done} /></div>

          <div className="min-w-0 flex-1">
            <h3 className={`text-[15px] font-semibold tracking-tight ${step.done ? 'text-muted' : 'text-ink'}`}>
              {step.title}
            </h3>
            <p className="mt-1 text-[13.5px] leading-relaxed text-ink-soft">{step.blurb}</p>

            {step.detail && (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11.5px] font-medium text-emerald-700">
                <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                {step.detail}
              </p>
            )}
          </div>

          {!step.done && step.href && (
            <Link to={step.href} className="btn btn-primary btn-sm shrink-0">
              {step.cta ?? 'Open'} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </CardBody>
      </Card>
    </motion.div>
  );
}

export function SetupPage() {
  const toast = useToast();
  const { data, error, loading, refetch, setData } = useResource('/dashboard/setup');

  const dismiss = useMutation(async () => {
    const next = await http.post('/dashboard/setup/dismiss', {});
    setData(next);
    toast.success('Setup hidden', { description: 'You can still reach it from this page whenever you like.' });
  });

  const required = data?.steps.filter((s) => s.required) ?? [];
  const optional = data?.steps.filter((s) => !s.required) ?? [];

  return (
    <>
      <PageHero
        icon={Rocket}
        chip="Getting started"
        title={data?.complete ? 'You’re set up' : 'Let’s get you paid'}
        blurb={
          data?.complete
            ? 'Everything you need is in place. What’s left below is optional — worth doing, but nothing is waiting on it.'
            : 'A short list, and only the first few actually stop a customer paying you. Each one ticks itself when it’s genuinely done.'
        }
        actions={
          data && !data.dismissed && !data.complete ? (
            <Button variant="secondary" icon={X} loading={dismiss.loading} onClick={() => dismiss.mutate().catch(() => {})}>
              Hide this
            </Button>
          ) : undefined
        }
      />

      {loading && <Loading label="Checking what’s done…" />}
      {error && <ErrorNotice error={error} onRetry={refetch} />}

      {data && (
        <div className="space-y-6">
          {/* ---- the headline answer: can you be paid right now? ---- */}
          <Card>
            <CardBody className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <span
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border"
                  style={{
                    borderColor: data.readyToTakePayments ? 'rgb(16 185 129 / 0.35)' : 'rgb(var(--line))',
                    background: data.readyToTakePayments ? 'rgb(236 253 245)' : 'rgb(var(--surface-2))',
                  }}
                >
                  {data.readyToTakePayments
                    ? <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                    : <Sparkles className="h-5 w-5 text-ink-soft" aria-hidden="true" />}
                </span>
                <div>
                  <p className="text-[15px] font-semibold text-ink">
                    {data.readyToTakePayments
                      ? 'You can take payments'
                      : 'You can’t take payments yet'}
                  </p>
                  <p className="mt-0.5 text-[13px] text-muted">
                    {data.readyToTakePayments
                      ? 'At least one payment method is switched on and fully configured.'
                      : 'Choose how you get paid below — a provider, or your own bank details.'}
                  </p>
                </div>
              </div>

              <div className="tabular text-right">
                <div className="text-[22px] font-semibold leading-none text-ink">
                  {data.requiredDone}<span className="text-muted">/{data.requiredTotal}</span>
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-wide text-faint">Essentials</div>
              </div>
            </CardBody>
          </Card>

          {/* ---- the steps that actually block you ---- */}
          <div>
            <h2 className="mb-3 text-[11px] font-medium uppercase tracking-[0.14em] text-faint">
              Before a customer can pay you
            </h2>
            <div className="space-y-3">
              {required.map((step, i) => <StepCard key={step.key} step={step} index={i} />)}
            </div>
          </div>

          {/* ---- and the ones that are genuinely optional ---- */}
          <div>
            <h2 className="mb-1 text-[11px] font-medium uppercase tracking-[0.14em] text-faint">
              Worth doing, when you need it
            </h2>
            <p className="mb-3 text-[13px] text-muted">
              None of this is required. If you’re using payment links and the till, you can ignore all of it.
            </p>
            <div className="space-y-3">
              {optional.map((step, i) => <StepCard key={step.key} step={step} index={i} />)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SetupPage;
